/**
 * BulkImportDialog - Dialog for bulk importing users via CSV
 * Extracted from SettingsPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Download,
  Upload,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";

export function BulkImportDialog({
  open,
  onOpenChange,
  csvData,
  setCsvData,
  importPreview,
  importResult,
  importing,
  onPreview,
  onImport,
  onDownloadTemplate,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Users</DialogTitle>
          <DialogDescription>
            Import multiple users at once using CSV format
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Template Download */}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">CSV Template</p>
                <p className="text-xs text-slate-500">Download and fill out the template</p>
              </div>
              <Button variant="outline" size="sm" className="gap-2" onClick={onDownloadTemplate}>
                <Download className="w-4 h-4" />
                Download Template
              </Button>
            </div>
          </div>

          {/* CSV Input */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Paste CSV Data</p>
            <Textarea
              value={csvData}
              onChange={(e) => setCsvData(e.target.value)}
              placeholder="name,email,role,phone
John Smith,john@example.com,case_manager,555-123-4567
Jane Doe,jane@example.com,rn,555-987-6543"
              rows={6}
              className="font-mono text-xs"
            />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onPreview} disabled={!csvData.trim()}>
                Preview Import
              </Button>
            </div>
          </div>

          {/* Import Preview */}
          {importPreview && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-blue-500" />
                <p className="text-sm font-medium text-slate-700">
                  Found {importPreview.total} users to import
                  {importPreview.errors?.length > 0 && (
                    <span className="text-red-500 ml-2">
                      ({importPreview.errors.length} with errors)
                    </span>
                  )}
                </p>
              </div>
              
              {/* Errors */}
              {importPreview.errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
                  <ul className="text-xs text-red-600 space-y-1">
                    {importPreview.errors.map((err, idx) => (
                      <li key={idx}>Row {err.row}: {err.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Valid Users Preview */}
              {importPreview.valid_users?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                        <TableHead className="text-xs">Role</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importPreview.valid_users.slice(0, 5).map((user, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-xs">{user.name}</TableCell>
                          <TableCell className="text-xs">{user.email}</TableCell>
                          <TableCell className="text-xs capitalize">{user.role?.replace("_", " ")}</TableCell>
                          <TableCell>
                            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                              Ready
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {importPreview.valid_users.length > 5 && (
                    <p className="text-xs text-slate-500 p-2 text-center border-t">
                      And {importPreview.valid_users.length - 5} more...
                    </p>
                  )}
                </div>
              )}

              {/* Billing Impact */}
              {importPreview.billing_impact && importPreview.billing_impact.cost_increase > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-700 mb-1">Billing Impact</p>
                  <p className="text-xs text-amber-600">
                    This import will add ${importPreview.billing_impact.cost_increase.toFixed(2)}/month to your bill
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className={`p-4 rounded-lg border ${
              importResult.success ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {importResult.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <p className={`font-medium ${importResult.success ? "text-emerald-700" : "text-red-700"}`}>
                  {importResult.success ? "Import Successful!" : "Import Failed"}
                </p>
              </div>
              {importResult.success && (
                <div className="text-sm text-emerald-600 space-y-1">
                  <p>✓ {importResult.users_created} users created</p>
                  {importResult.skipped > 0 && (
                    <p>⚠ {importResult.skipped} users skipped (already exist)</p>
                  )}
                </div>
              )}
              {!importResult.success && importResult.message && (
                <p className="text-sm text-red-600">{importResult.message}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {importResult?.success ? "Done" : "Cancel"}
          </Button>
          {!importResult?.success && importPreview?.valid_users?.length > 0 && (
            <Button onClick={onImport} disabled={importing} className="gap-2">
              <Upload className="w-4 h-4" />
              {importing ? "Importing..." : `Import ${importPreview.valid_users.length} Users`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
